import { createFileRoute } from '@tanstack/react-router';
import { PricingTable, useCustomer, usePricingTable } from 'autumn-js/react';
import { useState } from 'react';
import AttachDialog from '@/components/autumn/attach-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import CreditStatusCard from '@/components/Navbar/components/CreditStatusCard';

const DEMO_URL = 'https://cal.com/nika19/15min-chat-greetai';
const ACTIVE_STATUSES = new Set(['active', 'trialing', 'past_due', 'scheduled']);

type CustomerProduct = {
  id: string;
  name?: string | null;
  status?: string;
  is_default?: boolean;
  current_period_end?: number | null;
  canceled_at?: number | null;
};

type PricingProduct = {
  id: string;
  name?: string | null;
  is_default?: boolean;
};

export const Route = createFileRoute('/_auth/billing')({
  component: RouteComponent,
});

function formatDate(timestamp?: number | null) {
  if (!timestamp) {
    return null;
  }

  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getActiveProducts(products?: CustomerProduct[]) {
  return products?.filter((product) => ACTIVE_STATUSES.has(product.status ?? '')) ?? [];
}

function getPrimaryProduct(products?: CustomerProduct[]) {
  const activeProducts = getActiveProducts(products);

  return (
    activeProducts.find((product) => !product.is_default) ??
    activeProducts[0] ??
    products?.find((product) => !product.is_default) ??
    products?.[0]
  );
}

function getVisiblePricingProductDetails(products?: PricingProduct[]) {
  let hasShownFreePlan = false;

  return products
    ?.filter((product) => {
      if (product.id === 'super') {
        return false;
      }

      const productName = (product.name ?? product.id).toLowerCase();
      const isFreePlan = product.is_default || productName.includes('free');

      if (!isFreePlan) {
        return true;
      }

      if (hasShownFreePlan) {
        return false;
      }

      hasShownFreePlan = true;
      return true;
    })
    .map((product) => ({ id: product.id }));
}

function RouteComponent() {
  const { customer, cancel, refetch } = useCustomer();
  const {
    products: pricingProducts,
    isLoading: isPricingTableLoading,
    error: pricingTableError,
  } = usePricingTable();
  const [isCancelling, setIsCancelling] = useState(false);

  const currentProduct = getPrimaryProduct(
    customer?.products as CustomerProduct[] | undefined,
  );
  const planName = currentProduct?.name || currentProduct?.id || 'Free plan';
  const nextBillingDate = formatDate(
    currentProduct?.current_period_end ?? currentProduct?.canceled_at,
  );
  const isCancellationScheduled = Boolean(currentProduct?.canceled_at);
  const pricingProductDetails = getVisiblePricingProductDetails(
    pricingProducts as PricingProduct[] | undefined,
  );

  const handleCancel = async () => {
    if (!currentProduct || isCancelling || isCancellationScheduled) {
      return;
    }

    const shouldCancel = window.confirm(
      `Cancel ${planName}? Your plan will stay active until the end of the current billing cycle.`,
    );

    if (!shouldCancel) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancel({ productId: currentProduct.id });
      await refetch();
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-5">
      <div className="grid gap-3">
        <Card className="rounded-sm border-border/70 bg-card p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="inline-flex w-fit rounded-sm border border-border/80 bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                  Current plan
                </span>
                <span className="text-sm font-medium text-muted-foreground">
                  {isCancellationScheduled ? 'Ending soon' : 'Active'}
                </span>
              </div>
              <h2 className="mt-2 text-base font-semibold text-foreground">{planName}</h2>

              {nextBillingDate ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {isCancellationScheduled
                    ? `Access until ${nextBillingDate}.`
                    : `Renews ${nextBillingDate}.`}
                </p>
              ) : null}
            </div>

            {currentProduct ? (
              <div className="shrink-0">
                <Button
                  onClick={handleCancel}
                  disabled={isCancelling || isCancellationScheduled}
                  variant="outline"
                  className="bg-card px-4"
                >
                  {isCancellationScheduled
                    ? 'Cancellation scheduled'
                    : isCancelling
                      ? 'Cancelling...'
                      : 'Cancel plan'}
                </Button>
              </div>
            ) : null}
          </div>
        </Card>

        <CreditStatusCard />
      </div>

      {pricingTableError ? (
        <div className="rounded-sm border border-border/70 bg-muted/40 p-5">
          <p className="text-sm text-muted-foreground">
            Unable to load plans right now. Please try again in a moment.
          </p>
        </div>
      ) : isPricingTableLoading || !pricingProductDetails?.length ? (
        <div className="rounded-sm border border-border/70 bg-muted/40 p-5">
          <p className="text-sm text-muted-foreground">Loading plans...</p>
        </div>
      ) : (
        <PricingTable productDetails={pricingProductDetails} />
      )}

      <Card className="rounded-sm border-border/70 bg-card p-4 shadow-sm mt-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-foreground">Need a larger setup?</h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              We can tailor higher monthly session credits, more concurrent sessions, and extra seats for your team.
            </p>
          </div>
          <Button asChild className="bg-neutral-900 px-5 text-white hover:bg-neutral-800">
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
              Request custom plan
            </a>
          </Button>
        </div>
      </Card>
    </div>
  );
}
